// Derives every gameplay telemetry event from useGameStore phase transitions
// — no game action is edited to call into telemetry directly. Two hazards
// specific to this approach, both handled below:
//
// 1. Zustand's subscribe callback runs AFTER the state update. The
//    transitions that end a run are exactly the ones that also wipe the
//    run's data (resetGame() and every startXxx() spread freshRunState()) —
//    so the run_end payload MUST be built from `prevState`, never from
//    `useGameStore.getState()` or even the `state` the callback receives,
//    for any field freshRunState() resets. (lastRunPassed/lastRunPhaseIndex/
//    lastRunThreshold/lastSurvivalWaves/lastSurvivalTrackIndex are the
//    exception — completeCampaignRun/completeSurvivalRun compute those in
//    the very same set() call that changes phase, so by the time this
//    callback runs they're already correct on `state`.)
// 2. A "retry" transition (paused → countdown/teach) is simultaneously the
//    end of the abandoned run and the start of a new one — both events fire
//    from the same callback invocation, in that order.
import { track } from "./telemetry";
import { buildRunEndProps, type RunSnapshot } from "./runSummary";
import type { RunOutcome } from "./events";
import { useGameStore, type GameMode, type Phase } from "../game/useGameStore";

const RUN_START_SOURCES: ReadonlySet<Phase> = new Set([
  "menu",
  "survival-intro",
  "story",
  "win",
  "fail",
  "paused",
]);

let playingStartedAt: number | null = null;
// Defense-in-depth against double-emitting run_end for one run — see hazard
// #1 above. The phase-pair rules below are designed not to need this, but
// the guard is one boolean and mirrors the idempotency pattern already used
// at GameManager.tsx's wonRef / SurvivalManager.tsx's completed.current.
let runEndPending = false;

function snapshotFrom(state: {
  mode: GameMode;
  score: number;
  peakCombo: number;
  gradeCounts: import("../game/useGameStore").GradeCounts;
  runKataLanded: number;
  runKataTotal: number;
  runKataBonus: number;
  runTargetEvents: import("../game/useGameStore").TargetRunEvent[];
  runObstacleEvents: import("../game/useGameStore").ObstacleRunEvent[];
  lastRunPassed: boolean | null;
  lastRunPhaseIndex: number;
  lastRunThreshold: number;
  lastSurvivalWaves: number;
  lastSurvivalTrackIndex: number;
}): RunSnapshot {
  return {
    mode: state.mode,
    score: state.score,
    peakCombo: state.peakCombo,
    gradeCounts: state.gradeCounts,
    runKataLanded: state.runKataLanded,
    runKataTotal: state.runKataTotal,
    runKataBonus: state.runKataBonus,
    runTargetEvents: state.runTargetEvents,
    runObstacleEvents: state.runObstacleEvents,
    lastRunPassed: state.lastRunPassed,
    lastRunPhaseIndex: state.lastRunPhaseIndex,
    lastRunThreshold: state.lastRunThreshold,
    lastSurvivalWaves: state.lastSurvivalWaves,
    lastSurvivalTrackIndex: state.lastSurvivalTrackIndex,
  };
}

function emitRunEnd(
  accumulatorSource: RunSnapshot,
  outcome: RunOutcome,
  overrides?: Partial<
    Pick<
      RunSnapshot,
      | "lastRunPassed"
      | "lastRunPhaseIndex"
      | "lastRunThreshold"
      | "lastSurvivalWaves"
      | "lastSurvivalTrackIndex"
    >
  >,
) {
  if (!runEndPending) return;
  runEndPending = false;
  const durationS =
    playingStartedAt != null
      ? (performance.now() - playingStartedAt) / 1000
      : 0;
  playingStartedAt = null;
  const snapshot: RunSnapshot = { ...accumulatorSource, ...overrides };
  track("run_end", buildRunEndProps(snapshot, outcome, durationS));
}

function emitRunStart(mode: GameMode, phaseIndex: number | null) {
  runEndPending = true;
  playingStartedAt = null;
  track("run_start", { mode, phase_index: phaseIndex });
}

export function bindTelemetry() {
  useGameStore.subscribe((state, prevState) => {
    if (state.phase === prevState.phase) return;
    const prevPhase = prevState.phase;
    const nextPhase = state.phase;

    // Mark the moment gameplay actually begins — countdown → playing only,
    // never paused → playing (resume), so a pause doesn't reset the clock.
    if (nextPhase === "playing" && prevPhase === "countdown") {
      playingStartedAt = performance.now();
    }

    // Campaign clear/short-of-threshold. completeCampaignRun() doesn't touch
    // the run accumulators (score, runTargetEvents, …) in this same set()
    // call, only the freshly-computed lastRunPassed/etc — prevState and
    // state agree on the former, so prevState is used uniformly for safety.
    if (prevPhase === "playing" && nextPhase === "win") {
      emitRunEnd(
        snapshotFrom(prevState),
        state.lastRunPassed ? "clear" : "short",
        {
          lastRunPassed: state.lastRunPassed,
          lastRunPhaseIndex: state.lastRunPhaseIndex,
          lastRunThreshold: state.lastRunThreshold,
        },
      );
    }

    // Survival's only way to end (miss-limit reached).
    if (prevPhase === "playing" && nextPhase === "fail") {
      emitRunEnd(snapshotFrom(prevState), "fail", {
        lastSurvivalWaves: state.lastSurvivalWaves,
        lastSurvivalTrackIndex: state.lastSurvivalTrackIndex,
      });
    }

    // Quit mid-run. The only source in the codebase today is exitToMenu()
    // (App.tsx), reachable solely from "paused" — "playing" is included
    // defensively in case another path reaches "menu" directly. Either way
    // freshRunState() has already wiped the accumulators on `state`, so this
    // MUST read prevState.
    if (
      (prevPhase === "playing" || prevPhase === "paused") &&
      nextPhase === "menu"
    ) {
      emitRunEnd(snapshotFrom(prevState), "quit");
    }

    // Retry from pause (restartRun()): the same transition both abandons the
    // current run (quit) and immediately starts the next one.
    if (
      prevPhase === "paused" &&
      (nextPhase === "countdown" || nextPhase === "teach")
    ) {
      emitRunEnd(snapshotFrom(prevState), "quit");
    }

    // Any genuine "about to play" entry — first start, retry from the
    // result screen, or the next campaign phase after a story beat.
    if (
      (nextPhase === "countdown" || nextPhase === "teach") &&
      RUN_START_SOURCES.has(prevPhase)
    ) {
      emitRunStart(
        state.mode,
        state.mode === "campaign" ? state.selectedPhaseIndex : null,
      );
    }

    // Coarse navigation funnel — only from the main menu, so opening a
    // sub-screen mid-run (there isn't one) can't be conflated with this.
    if (
      prevPhase === "menu" &&
      (nextPhase === "kata" ||
        nextPhase === "story" ||
        nextPhase === "survival-intro")
    ) {
      track("menu_nav", { screen: nextPhase });
    }
  });
}
